/**
 * AI Prompt Template Utilities
 * Provides a structured way to build prompts with consistent sections and formatting.
 * 
 * This is the canonical module for prompt utilities. All prompt files should import from here.
 */

export const COMMON_GUARDRAILS = `
GUARDRAILS:
- Use ONLY the data provided in the prompt. Do NOT invent facts or sources.
- If required info is missing, explicitly write 'Not provided' or omit that part.
- Do NOT create any new placeholders. Preserve any placeholders you do not recognize unchanged.
- Follow the requested output format exactly. Do NOT add explanations or code fences (unless specified).
`;

/**
 * Standard Chain-of-Thought (CoT) instructions to improve reasoning.
 */
export const COT_INSTRUCTIONS = `
Take a deep breath and think step-by-step. 
1. Analyze the input data efficiently.
2. Identify the core constraints and goals.
3. Draft your response mentally before generating the final output.
`;

/**
 * Helper to format a "Few Shot" example section.
 * @param {Array<{input: string, output: string}>} examples 
 */
export const formatFewShotExamples = (examples) => {
    if (!examples || examples.length === 0) return '';

    return `
**EXAMPLES:**
${examples.map((ex, i) => `
--- Example ${i + 1} ---
INPUT:
${ex.input}

OUTPUT:
${ex.output}
`).join('\n')}
--- End Examples ---
`;
};

/**
 * Builds a structured prompt from named sections.
 * @param {Object} sections - Key-value pairs of section names and content.
 * @returns {string} - The fully assembled prompt.
 */
export const buildStructuredPrompt = (sections) => {
    return Object.entries(sections)
        .filter(([_, content]) => content) // Remove empty sections
        .map(([title, content]) => `**${title.toUpperCase()}:**\n${content}`)
        .join('\n\n');
};

/**
 * Minimal prompt builder: join parts with clear spacing.
 * Use this for simple free-form prompt composition.
 * @param {...string|string[]} parts - Prompt sections to join
 * @returns {string} - The assembled prompt.
 */
export const buildPrompt = (...parts) => {
    const flattened = parts.flat();
    return flattened.filter(Boolean).join('\n\n');
};

/**
 * Placeholder for future prompt variable validation.
 * Returns true to avoid blocking; extend this for production validation.
 * @param {string} template - Prompt template
 * @param {Object} variables - Variables to validate
 * @returns {boolean}
 */
export const validatePromptVariables = (template, variables) => {
    return true;
};

export const PromptTemplates = {
    COMMON_GUARDRAILS,
    COT_INSTRUCTIONS,
    formatFewShotExamples,
    buildStructuredPrompt,
    buildPrompt,
    validatePromptVariables
};

export default PromptTemplates;
